import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Phone, ChevronDown } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";

import heroImage from "@/assets/custom-masonry-driveway-cedar-grove-nj.webp";
import stoneMasonryOakland from "@/assets/stone-masonry-installation-oakland-nj.webp";
import outdoorKitchenTotowa from "@/assets/outdoor-kitchen-installation-totowa-nj.webp";
import customMasonryHomepage from "@/assets/custom-masonry-homepage-north-caldwell-nj.webp";

const CitationLink = ({ num, url, children }: { num: number; url: string; children?: React.ReactNode }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
    {children}<sup>[{num}]</sup>
  </a>
);

const faqs = [
  {
    question: "Do I need a permit for masonry work in North Jersey?",
    answer: "Most structural masonry projects in New Jersey require a construction permit through your local building department. The NJ Uniform Construction Code (UCC) governs these requirements. Contact your municipal construction office before work begins to confirm what your project needs.",
  },
  {
    question: "What types of custom masonry projects work best for North Jersey homes?",
    answer: "Brick stoops, stone retaining walls, outdoor fireplaces, and facade restorations are the most requested projects in our area. The right material depends on your home's architecture, your property layout, and how the space will be used. We can walk you through the options during a site visit.",
  },
  {
    question: "How do I know if my masonry needs repair or full replacement?",
    answer: "Cracked mortar joints, loose bricks, leaning walls, or visible water stains are the main warning signs. A mason can evaluate the condition and tell you whether repointing, a partial rebuild, or full replacement makes the most sense. Catching it early usually means a simpler fix.",
  },
  {
    question: "When is the best time to schedule masonry work in North Jersey?",
    answer: "Late spring through early fall gives you the best conditions for new work and repairs. Mortar sets properly when temperatures stay above 40°F for at least 48 hours after installation. We start booking spring projects in late winter, so planning ahead helps.",
  },
  {
    question: "How long does custom masonry last in the North Jersey climate?",
    answer: "Quality masonry built with the right materials and proper technique lasts 75 to 100+ years. Freeze-thaw cycles and salt exposure do require periodic maintenance, but the structure itself is one of the most durable building methods you can choose.",
  },
  {
    question: "What is the difference between brick, block, and stone masonry?",
    answer: "Brick is fired clay, great for facades, walls, and decorative features. Concrete block (CMU) is structural and cost-effective for below-grade and load-bearing work. Natural stone offers the most custom look and is used for veneers, garden walls, and accent features. Each material has its place depending on your project goals.",
  },
];

const citations = [
  {
    num: 1,
    title: "Mason Contractors Association of America (MCAA)",
    url: "https://masoncontractors.org/mcaa/",
  },
  {
    num: 2,
    title: "ASTM C270 — Mortar for Unit Masonry",
    url: "https://store.astm.org/standards/c270",
  },
  {
    num: 3,
    title: "Brick Industry Association (BIA) — Technical Notes",
    url: "https://www.gobrick.com/resources/technical-notes",
  },
  {
    num: 4,
    title: "NJ Dept. of Community Affairs — Construction Permits",
    url: "https://www.nj.gov/dca/codes/resources/constructionpermitforms.shtml",
  },
  {
    num: 5,
    title: "OSHA — MCAA Alliance for Masonry Safety",
    url: "https://www.osha.gov/alliances/mcaa/mcaa",
  },
];

const masonryTypes = [
  {
    title: "Brick Masonry",
    description: "Walls, facades, columns, fireplace surrounds. Brick gives you a classic look with strong structural performance. Works well on both older homes and new construction.",
  },
  {
    title: "Block Masonry",
    description: "Concrete masonry units (CMU) for structural walls, retaining walls, and foundations. Cost-effective and strong, common in below-grade work and load-bearing applications.",
  },
  {
    title: "Natural Stone Masonry",
    description: "Bluestone, limestone, and fieldstone for veneers, garden walls, and accent features. Stone gives properties a custom feel that stands apart from standard finishes.",
  },
];

const commonProjects = [
  "Brick or stone accent walls",
  "Outdoor kitchens and grill surrounds",
  "Stone entryways and front stoops",
  "Garden walls and landscape borders",
  "Decorative columns and mailbox surrounds",
];

const commonFailures = [
  {
    title: "Cracking from Improper Joints",
    description: "Missing or poorly placed expansion joints allow thermal movement to crack the masonry over time.",
  },
  {
    title: "Efflorescence",
    description: "White salt deposits on brick surfaces caused by water carrying soluble salts through the masonry.",
  },
  {
    title: "Water Penetration",
    description: "Poorly sealed joints or missing flashing allow water into the wall assembly, leading to interior damage.",
  },
  {
    title: "Mortar Deterioration",
    description: "Using the wrong mortar type for the conditions causes joint failure and accelerates freeze-thaw damage.",
  },
];

export default function CustomMasonry() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Custom Masonry Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Custom brick, block, and stone masonry for North Jersey homes. Free consultations, over 35 years of experience. Call Masonry Pro Construction today for a quote." />
        <meta property="og:description" content="Custom brick, block, and stone masonry for North Jersey homes. Free consultations, over 35 years of experience. Call Masonry Pro Construction today for a quote." />
        <meta property="og:title" content="Custom Masonry Totowa, NJ | Masonry Pro Construction" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Custom Masonry",
            description: "Custom brick, block, and stone masonry for North Jersey homes. Over 35 years of experience.",
            url: "/masonry-services/custom-masonry",
            image: "https://masonry-pro.com/og/custom-masonry.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/custom-masonry.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/custom-masonry.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Custom masonry design and installation in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Custom Masonry in Totowa, NJ Serving <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>North Jersey</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Brick, block, and natural stone work for walls, facades, outdoor features, and structural projects. Built to last for decades.
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="tel:+19737252395" className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    Call or Text (973) 725-2395
                  </a>
                </Button>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button
                onClick={() => document.getElementById("intro")?.scrollIntoView({ behavior: "smooth" })}
                className="text-secondary hover:text-secondary/80 transition-colors animate-bounce"
                aria-label="Scroll down"
              >
                <ChevronDown className="h-10 w-10" />
              </button>
            </div>
          </section>

          {/* Intro Section */}
          <section id="intro" className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto space-y-6 text-lg text-muted-foreground">
                <p>
                  Custom masonry in North Jersey adds lasting value to homes and commercial properties. Every project starts with a free consultation and site visit. We are a full-service masonry contractor, and we build structures designed to hold up for decades. Custom work is where a <Link to="/" className="text-secondary hover:underline">family-owned masonry contractor</Link> can take the time to get the details right.
                </p>
                <p>
                  Older homes in towns like Montclair, Morristown, and Ridgewood often feature original brick or stone. Restoration work on these properties takes a mason who can match the existing materials. Color, texture, and mortar profile all need to line up with what is already there.
                </p>
              </div>
            </div>
          </section>

          {/* Masonry Types Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                Custom Masonry Covers <span className="text-gradient-gold">Brick, Block, and Natural Stone</span>
              </h2>
              <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto mb-12">
                Which material fits your project depends on load requirements, climate exposure, and the look you want. <CitationLink num={2} url="https://store.astm.org/standards/c270">ASTM standards</CitationLink> govern the mortar and unit specs used across all three types.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {masonryTypes.map((type, index) => (
                  <div key={type.title} className="bg-card rounded-xl p-6 border border-border flex flex-col">
                    <div className="w-10 h-10 rounded-full bg-gradient-navy flex items-center justify-center mb-4 flex-shrink-0">
                      <span className="text-secondary font-bold">{index + 1}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-3">{type.title}</h3>
                    <p className="text-muted-foreground">{type.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* What Masons Do Section — TEXT LEFT, IMAGE RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Skilled Masons Build Structures <span className="text-gradient-gold">That Last for Generations</span>
                  </h2>
                  <div className="space-y-5 text-lg text-muted-foreground">
                    <p>
                      What does a mason actually do on your job site? More than most people expect. Layout comes first: measuring, marking, and planning the structure before a single unit gets set. Then comes cutting, mortar mixing, setting each brick or stone, and finishing the joints. Every step affects how the finished product looks and how long it lasts.
                    </p>
                    <p>
                      Mortar selection alone makes a big difference. Masons choose from Type M, S, N, or O mortar depending on the project. A retaining wall below grade needs a different mix than a decorative garden wall. Getting that wrong leads to cracking or joint failure down the road.
                    </p>
                    <p>
                      The <CitationLink num={1} url="https://masoncontractors.org/mcaa/">Mason Contractors Association of America (MCAA)</CitationLink> promotes continuing education and code-based training for professional masons. Historic buildings in Paterson and Passaic still stand today because of quality workmanship, but repairing them takes someone who understands legacy construction methods.
                    </p>
                  </div>
                </div>

                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img
                    src={stoneMasonryOakland}
                    alt="Custom stone masonry installation in Oakland, Bergen County, NJ by Masonry Pro Construction"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Property Benefits Section — IMAGE LEFT, TEXT RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img
                    src={outdoorKitchenTotowa}
                    alt="Outdoor kitchen with brick pizza oven and granite countertops in Totowa, Passaic County, NJ by Masonry Pro Construction"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    North Jersey Properties Benefit from <span className="text-gradient-gold">Custom-Designed Masonry</span>
                  </h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    In North Jersey's competitive real estate market, exterior finishes matter. Custom brick facades, stone entryways, and well-built front stoops give a home an edge. Neighborhoods like Maplewood and Glen Ridge reward that kind of quality.
                  </p>

                  <h3 className="text-xl font-bold mb-4">Common Custom Projects We Handle</h3>
                  <ul className="space-y-3 mb-6">
                    {commonProjects.map((project) => (
                      <li key={project} className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0" />
                        <span className="text-lg text-muted-foreground">{project}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="p-5 bg-card rounded-xl border border-border">
                    <p className="text-muted-foreground">
                      Beyond curb appeal, masonry resists fire, doesn't attract pests, and requires far less upkeep over time compared to wood or vinyl. The <CitationLink num={3} url="https://www.gobrick.com/resources/technical-notes">Brick Industry Association (BIA)</CitationLink> publishes technical notes on residential masonry performance and long-term durability.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Common Failures Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                Professional Installation <span className="text-gradient-gold">Prevents Common Masonry Failures</span>
              </h2>
              <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto mb-12">
                Masonry is durable, but it is not bulletproof. When things go wrong, it's almost always tied to how the work was done.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-10">
                {commonFailures.map((failure, index) => (
                  <div key={index} className="bg-background rounded-xl p-6 border border-border flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-full bg-gradient-navy flex items-center justify-center flex-shrink-0">
                      <span className="text-secondary font-bold">{index + 1}</span>
                    </div>
                    <div>
                      <h4 className="font-bold mb-1">{failure.title}</h4>
                      <p className="text-muted-foreground text-sm">{failure.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="max-w-4xl mx-auto p-6 bg-muted rounded-xl">
                <p className="text-lg text-muted-foreground">
                  <strong>North Jersey's climate makes this even more of a factor.</strong> The region averages 60 to 80 freeze-thaw days per year. Water gets into mortar joints, freezes, expands, and pushes the joint apart. Over a few seasons, that damage adds up fast. <CitationLink num={2} url="https://store.astm.org/standards/c270">ASTM C270</CitationLink> defines the mortar types and performance standards designed to prevent these problems.
                </p>
              </div>
            </div>
          </section>

          {/* Maintenance Section — TEXT LEFT, IMAGE RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Seasonal Maintenance Protects <span className="text-gradient-gold">Your Masonry Investment</span>
                  </h2>
                  <div className="space-y-5 text-lg text-muted-foreground">
                    <p>
                      An annual visual inspection is a good place to start. Look at your mortar joints, wall surfaces, and drainage paths. Cracks, crumbling mortar, or standing water near the base of a wall are all signs that something needs attention.
                    </p>
                    <p>
                      If mortar joints are failing, repointing is the fix. The new mortar has to match the original in strength and composition. Using a harder mortar on softer brick creates stress that causes more damage. A qualified mason knows how to test and match it properly.
                    </p>
                    <p>
                      <strong>Winter salt exposure is a big one across North Jersey.</strong> In towns like Wayne, Hackensack, and Nutley, walkways, steps, and retaining walls take a beating every season. Sealing exposed horizontal surfaces before winter helps reduce water absorption and slow that process. Catching small problems early saves you from bigger repairs later.
                    </p>
                  </div>
                </div>

                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img
                    src={customMasonryHomepage}
                    alt="Custom masonry work by Masonry Pro Construction serving North Jersey"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Featured Snippet Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                  What is Custom Masonry <span className="text-gradient-gold">in North Jersey?</span>
                </h2>
                
                <div className="bg-background rounded-xl p-8 border border-secondary/30">
                  <p className="text-lg text-muted-foreground mb-6">
                    Custom masonry is the design and construction of structures using brick, concrete block, or natural stone. In North Jersey, masons build and repair walls, facades, chimneys, steps, patios, and decorative features. Materials and methods are selected to handle the region's freeze-thaw cycles.
                  </p>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 bg-muted rounded-lg">
                      <h4 className="font-bold mb-2">Brick Masonry</h4>
                      <p className="text-sm text-muted-foreground">Walls, facades, columns, and fireplace surrounds</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <h4 className="font-bold mb-2">Stone Masonry</h4>
                      <p className="text-sm text-muted-foreground">Natural stone veneers, garden walls, and accent features</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <h4 className="font-bold mb-2">Block Masonry</h4>
                      <p className="text-sm text-muted-foreground">Structural walls, retaining walls, and foundations</p>
                    </div>
                  </div>
                  
                  <p className="text-muted-foreground mt-6">
                    Mortar types are matched to each project per ASTM C270 standards. Custom work is tailored to each property's architecture and site conditions.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* YouTube Shorts Section */}
          <YouTubeShortsSection 
            videoIds={["KjTHv1gQP9c", "0RG6pVGG92E", "_8_1uw8ZHAk", "IaLfa_u0pFQ"]}
            title="See Our"
            highlightedTitle="Custom Masonry Work"
          />

          {/* FAQs Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              <p className="text-lg text-muted-foreground text-center max-w-2xl mx-auto mb-12">
                Common questions about custom masonry work in North Jersey
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-background rounded-xl p-6 border border-border flex flex-col">
                    <h3 className="font-bold mb-3 text-lg">{faq.question}</h3>
                    <p className="text-sm text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="masonry" hubOverride={{ href: "/", label: "Masonry Contractor in North Jersey" }} />

          {/* CTA Section */}
          <CTASection serviceName="custom masonry" />

          {/* Citations Section */}
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <Accordion type="single" collapsible className="max-w-4xl mx-auto">
                <AccordionItem value="citations" className="border-border">
                  <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                    Sources & Citations
                  </AccordionTrigger>
                  <AccordionContent>
                    <ol className="space-y-2 text-sm text-muted-foreground">
                      {citations.map((citation) => (
                        <li key={citation.num}>
                          [{citation.num}] <a href={citation.url} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">{citation.title}</a>
                        </li>
                      ))}
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
